import React, { useContext } from "react";
import { AxiosError } from "axios";
import { QueryResult, QueryKey } from "react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "fe-shared/src/types/common";
import { ReturnIcon } from "fe-shared/src/components/Icons";
import {
  LinkButton,
  Loader,
  CollapseComponent,
  NotFoundPage,
  RecordsSidebar,
} from "fe-shared/src/components";

import {
  InvoiceLink,
  DialogHeader,
} from "fe-shared-transactions/src/components";
import { Transfer } from "fe-shared-transactions/src/types";

import UserContext from "contexts/userContext";
import TransferStatus from "components/TransferStatus";
import TransferContentView from "components/TransferContentView";

import { TRANSFERS_ACTIVITIES_QUERY_KEY } from "../../consts";
import TransferActions from "./containers/TransferActions";

import * as api from "./modules/api";

import styles from "./TransferDetails.module.scss";

interface Props {
  returnUrl: string;
  transfer: QueryResult<Transfer, AxiosError<ApiError>>;
  transferQueryKey: QueryKey;
  hasSidebar?: boolean;
  canExportHistory?: boolean;
  canViewNotes?: boolean;
  canAddNote?: boolean;
  canExportNotes?: boolean;
  canEditTransfer?: boolean;
  canCancelTransfer?: boolean;
  canInvalidateTransfer?: boolean;
  canApproveTransfer?: boolean;
  canAssignTransfer?: boolean;
  canSeeLinkedBatches?: boolean;
  userEmail?: string;
}

const TransferDetails = (props: Props) => {
  const {
    returnUrl,
    transfer,
    hasSidebar,
    canExportHistory,
    canViewNotes,
    canAddNote,
    canExportNotes,
    userEmail,
    transferQueryKey,
    canEditTransfer,
    canCancelTransfer,
    canInvalidateTransfer,
    canApproveTransfer,
    canAssignTransfer,
    canSeeLinkedBatches,
  } = props;
  const { data, isLoading, isError } = transfer;

  const { t } = useTranslation();
  const currentUser = useContext(UserContext);

  const isTranferNotFound = transfer.error?.response?.status === 404;

  const isCurrentUserAssignee = data?.assignee?.userId === currentUser?.userId;

  const renderContent = () => {
    const { transferType, transferId, transferNumber } = data!;

    const activitiesQueryKey: QueryKey = [
      TRANSFERS_ACTIVITIES_QUERY_KEY,
      transferNumber,
    ];

    const forwardTraceabilityQueryKey: QueryKey = [
      TRANSFERS_ACTIVITIES_QUERY_KEY,
      transferNumber,
      "forward",
    ];

    const backwardTraceabilityQueryKey: QueryKey = [
      TRANSFERS_ACTIVITIES_QUERY_KEY,
      transferNumber,
      "backward",
    ];

    const getDialogTitle = (title: string) => (
      <DialogHeader
        type={transferType}
        title={title}
        number={transferNumber}
        translationRoot="transfers"
      />
    );

    return (
      <>
        <main className={styles.transferDetailsContent}>
          <div className={styles.transferDetailsContentWrapper}>
            <div className={styles.transferDetailsNavigation}>
              <LinkButton
                to={returnUrl}
                variant="inline"
                id="transfer-back-link"
                iconComponent={ReturnIcon}
              >
                {t("transfers.grid.title")}
              </LinkButton>
            </div>
            <div className={styles.transferDetailsData}>
              <TransferStatus transfer={data!} canAssign={canAssignTransfer} />
              <InvoiceLink
                prefix={t("invoices.linkPrefix")}
                invoiceNumber={data?.invoiceNumber}
                className={styles.transferDetailsInvoice}
              />
              <TransferContentView
                transfer={data!}
                canEditTransfer={canEditTransfer}
              />
              <TransferActions
                canApproveTransfer={isCurrentUserAssignee && canApproveTransfer}
                canCancelTransfer={canCancelTransfer}
                canInvalidateTransfer={canInvalidateTransfer}
                transferQueryKey={transferQueryKey}
                activitiesQueryKey={activitiesQueryKey}
                transfer={data!}
                isCurrentUserAssignee={isCurrentUserAssignee}
              />
            </div>
          </div>
        </main>
        {hasSidebar && (
          <CollapseComponent storageKey="sidebarCollapsed">
            <RecordsSidebar
              objectId={transferNumber}
              entityType="transfers"
              entityName={t("transfers.details.entityName")}
              initiatorEmail={userEmail || ""}
              getDialogTitle={getDialogTitle}
              fileNamePrefix={t("transfers.details.export.fileNamePrefix", {
                transferNumber,
              })}
              fileNameSuffix=""
              canViewHistory
              canExportHistory={canExportHistory}
              activitiesQueryKey={activitiesQueryKey}
              canViewNotes={canViewNotes}
              canAddNote={canAddNote}
              canExportNotes={canExportNotes}
              notesQueryKey={["transfersNotes", transferNumber]}
              getNotes={() => api.getTransferNotes(transferId)}
              addNote={(text) => {
                return api.addTransferNote({ transferId, text });
              }}
              canViewForwardTraceability={canSeeLinkedBatches}
              canViewBackwardTraceability={canSeeLinkedBatches}
              forwardTraceabilityQueryKey={forwardTraceabilityQueryKey}
              backwardTraceabilityQueryKey={backwardTraceabilityQueryKey}
              getBackwardTraceability={() => {
                return api.getLinkedTransfers({
                  transferNumber,
                  isTargetTransactionNext: false,
                });
              }}
              getForwardTraceability={() => {
                return api.getLinkedTransfers({
                  transferNumber,
                  isTargetTransactionNext: true,
                });
              }}
              transactionOrTransfer={data!}
            />
          </CollapseComponent>
        )}
      </>
    );
  };

  return (
    <div className={styles.transferDetails}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && data && renderContent()}
      {isError && (
        <NotFoundPage
          returnLink="/transfers"
          title={
            isTranferNotFound
              ? "transfers.details.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["transfers.details.error.description"]}
          returnLinkDescription="transfers.details.error.returnLink"
        />
      )}
    </div>
  );
};

export default TransferDetails;
